import {
  Callout,
  Checkbox,
  FormLayout,
  GridFieldGroup,
  Heading,
  Link,
  MaskedInput,
  Paragraph,
  Reservation,
  ReservationCard,
  Select,
  SubmitButton,
  TextInput,
} from '@expressable/syntax-ds';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSignupNavigationGuard } from 'domains/signupDirect/misc/useSignupNavigationGuard';
import config from 'config';
import { RelationshipEnum } from 'domains/client';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import InsuranceSections from 'domains/signupDirect/Evaluation/Insurance/InsuranceSections';
import PrivatePaySections from 'domains/signupDirect/Evaluation/PrivatePay/PrivatePaySections';
import { submitEvalForm } from 'domains/signupDirect/Evaluation/submitEvalForm';
import {
  getRelationshipOptions,
  INSURANCE_OPTION,
  OTHER_INSURANCE_OPTION,
  TherapyTargetValue,
  therapyTargetValues,
} from 'domains/signupDirect/misc/constants';
import {
  EvaluationForm,
  EvaluationFormType,
  getInsuranceSchemaType,
  paymentTypeIsInsurance,
} from 'domains/signupDirect/schemas/evaluationSchema';
import { InsurancePayment } from 'domains/signupDirect/schemas/filtersSchema';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { EligibilityCheckResult } from 'domains/signupv4/Insurance/types';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { get, noop } from 'lodash';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { getReservationScheduleProps } from 'utils/date';
import { useAutoScrollToTop } from 'utils/hooks/useAutoScrollToTop/useAutoScrollToTop';
import { usePeriodicEvaluationFormSave } from 'domains/signupDirect/misc/localStorage';
import { useState } from 'react';
import { TherapistReservationCard } from '../SyntaxComps/TherapistReservationCard';
import { useNavigate } from 'react-router-dom';
import { routes } from 'router/routes';

const getDefaultRelationshipToClient = (
  target: TherapyTargetValue,
): EvaluationFormType['contactInfo']['relationshipToClient'] | undefined => {
  switch (target) {
    case 'adult':
      return RelationshipEnum.Client;
    default:
      return undefined;
  }
};

const Component = () => {
  useAutoScrollToTop();
  const { filters, evaluationSchedule, incompleteEvalForm, resumedLeadState } = useSignupDirectCtx();
  const { t } = useTranslation();

  if (!filters) throw 'Expected filters to be set';
  if (!evaluationSchedule) throw 'Expected evalSelectedTimeSlot to be set';

  const paymentMethod = filters.paymentMethod;

  const methods = useForm<EvaluationFormType>({
    resolver: zodResolver(EvaluationForm),
    defaultValues: {
      leadId: resumedLeadState?.leadID,
      evaluationSchedule: evaluationSchedule,
      clientType: filters.therapyTarget,
      concerns: {
        clinicalAreas: filters.clinicalAreas,
        clinicalConcernKeys: filters.clinicalConcernKeys,
      },
      clientInfo: {
        language: filters.language,
        stateCode: filters.stateCode,
        firstName: incompleteEvalForm?.clientInfo?.firstName,
        lastName: incompleteEvalForm?.clientInfo?.lastName,
        dateOfBirth: incompleteEvalForm?.clientInfo?.dateOfBirth,
      },
      contactInfo: {
        relationshipToClient:
          incompleteEvalForm?.contactInfo?.relationshipToClient ??
          getDefaultRelationshipToClient(filters.therapyTarget),
        email: incompleteEvalForm?.contactInfo?.email,
        firstName: incompleteEvalForm?.contactInfo?.firstName,
        lastName: incompleteEvalForm?.contactInfo?.lastName,
        phone: incompleteEvalForm?.contactInfo?.phone,
      },
      billingInfo: {
        ...(paymentMethod.type === 'insurance' && {
          type: getInsuranceSchemaType(paymentMethod.insuranceData.insuranceType),
          insuranceData: (filters.paymentMethod as InsurancePayment).insuranceData,
          primaryPhysician:
            incompleteEvalForm?.billingInfo?.type === 'insurance-medicaid' ||
            incompleteEvalForm?.billingInfo?.type === 'insurance-medicare'
              ? incompleteEvalForm?.billingInfo?.primaryPhysician
              : undefined,
          eligibilityCheckResult: resumedLeadState?.shouldSkipEligibility
            ? EligibilityCheckResult.IN_NETWORK
            : (filters.paymentMethod as InsurancePayment).insuranceData.networkStatus === 'Out-of-network'
            ? EligibilityCheckResult.OUT_OF_NETWORK
            : undefined,
          subscriber:
            incompleteEvalForm?.billingInfo?.type === 'insurance-commercial'
              ? incompleteEvalForm?.billingInfo.subscriber
              : undefined,
          memberId: get(incompleteEvalForm, 'billingInfo.memberId'),
        }),
        ...(paymentMethod.type === 'privatePay' && {
          type: 'privatePay',
        }),
      } as EvaluationFormType['billingInfo'],
    },
  });

  const { handleSubmit } = methods;

  return (
    <>
      <div>
        <Heading level="h1">{t('directSignup.eval.form.title')}</Heading>
        <Paragraph size="lg" noDefaultMargins>
          {t('directSignup.eval.form.subTitle')}
        </Paragraph>
      </div>

      <FormProvider {...methods}>
        <FormLayout className="mobile-xs-d:gap-e40 tablet-d:gap-e56" onSubmit={handleSubmit(noop)}>
          <FormFields />
          <SubmitSection />
        </FormLayout>
      </FormProvider>
    </>
  );
};

const FormFields = () => {
  const { t } = useTranslation();
  const { register, watch, getValues } = useFormContext<EvaluationFormType>();
  const [showUpdateInfo, setShowUpdateInfo] = useState(false);
  const navigate = useNavigate();
  const {
    filters,
    evaluationSchedule: evalSelectedTimeSlot,
    onSaveIncompleteEvalForm,
    onContinueToScheduleEval,
    isResumedLead,
  } = useSignupDirectCtx();
  if (!filters) throw 'Expected filters to be defined';
  if (!evalSelectedTimeSlot) throw 'Expected evalSelectedTimeSlot to be defined';
  const { format } = useDisplayTimezone();
  const relationshipOptions = getRelationshipOptions(t);

  useSignupNavigationGuard(() => {
    const incompleteForm = getValues();
    onSaveIncompleteEvalForm(incompleteForm);
  });

  usePeriodicEvaluationFormSave();

  const onEditSession = (session: 'first' | 'second') => {
    const incompleteForm = getValues();
    onSaveIncompleteEvalForm(incompleteForm);

    navigate(routes.signupDirect.evaluation.schedule.split[session]);
  };

  return (
    <>
      {evalSelectedTimeSlot.type === 'single' && (
        <ReservationCard className="w-full" title={t('directSignup.eval.schedule.reservation.title')}>
          <Reservation
            title={t('directSignup.eval.schedule.reservation.appointment')}
            ctaLabel={isResumedLead ? undefined : t('directSignup.eval.schedule.reservation.cta')}
            onCtaClick={() => onContinueToScheduleEval()}
            {...getReservationScheduleProps({
              dateTime: evalSelectedTimeSlot.timeSlot.time,
              formatFn: format,
              therapistName: evalSelectedTimeSlot.timeSlot.therapistName,
            })}
          />
        </ReservationCard>
      )}

      {evalSelectedTimeSlot.type === 'split' && (
        <TherapistReservationCard
          firstSession={evalSelectedTimeSlot.firstTimeSlot.time}
          secondSession={evalSelectedTimeSlot.secondTimeSlot?.time}
          therapistName={evalSelectedTimeSlot.firstTimeSlot.therapistName}
          reservationSubtitle={t('directSignup.schedule.split.sessionType')}
          reservationTitle={t('directSignup.schedule.split.sessionName')}
          onEditFirstSession={() => onEditSession('first')}
          onEditSecondSession={() => onEditSession('second')}
        />
      )}

      <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.sections.clientInfo.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            {t('directSignup.sections.clientInfo.subTitle')}
          </Paragraph>
          {showUpdateInfo && (
            <Callout className="my-8" type="info">
              {t('directSignup.eligibility.tryAgainCallOut')}
            </Callout>
          )}
        </HeadingCombo>
        <GridFieldGroup>
          <TextInput
            disabled={isResumedLead}
            data-testid="client-first-name-input"
            label={t('directSignup.labels.clientFirstName')}
            {...register('clientInfo.firstName')}
          />
          <TextInput
            disabled={isResumedLead}
            data-testid="client-last-name-input"
            label={t('directSignup.labels.clientLastName')}
            {...register('clientInfo.lastName')}
          />

          <MaskedInput
            disabled={isResumedLead}
            hint={
              filters.paymentMethod.type === INSURANCE_OPTION || filters.paymentMethod.type === OTHER_INSURANCE_OPTION
                ? t('directSignup.labels.dob.insurance.hint')
                : filters.therapyTarget === therapyTargetValues[0]
                ? t('directSignup.labels.dob.privatePay.child.hint')
                : t('directSignup.labels.dob.privatePay.adult.hint')
            }
            data-testid="client-dob-input"
            label={t('directSignup.labels.dob')}
            {...register('clientInfo.dateOfBirth')}
            mask="MM/DD/YYYY"
            type="tel"
            showMask
            replacement={{
              M: /[0-1]|[0-9]/,
              D: /[0-3]|[0-9]/,
              Y: /\d/,
            }}
          />
        </GridFieldGroup>
      </FormLayout.Fields>

      <FormLayout.Fields>
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.sections.contactInfo.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            {t('directSignup.sections.contactInfo.subTitle')}
          </Paragraph>
        </HeadingCombo>
        <Select
          data-testid="contact-relationship-to-client-select"
          label={t('directSignup.labels.relationshipToClient')}
          name="contactInfo.relationshipToClient"
          options={relationshipOptions}
          disabled={filters?.therapyTarget === 'adult'}
        />
        <GridFieldGroup>
          {watch('contactInfo.relationshipToClient') !== RelationshipEnum.Client && (
            <>
              <TextInput
                data-testid="contact-first-name-input"
                label={t('directSignup.labels.contactFirstName')}
                {...register('contactInfo.firstName')}
              />
              <TextInput
                data-testid="contact-last-name-input"
                label={t('directSignup.labels.contactLastName')}
                {...register('contactInfo.lastName')}
              />
            </>
          )}

          <TextInput
            data-testid="contact-email-input"
            label={t('directSignup.labels.email')}
            type="email"
            {...register('contactInfo.email')}
          />
          <TextInput
            data-testid="contact-phone-input"
            label={t('directSignup.labels.phone')}
            type="tel"
            {...register('contactInfo.phone')}
          />
        </GridFieldGroup>
      </FormLayout.Fields>

      <PrivatePaySections />

      <InsuranceSections showUpdateInfo={showUpdateInfo} setShowUpdateInfo={setShowUpdateInfo} />
    </>
  );
};

const SubmitSection = () => {
  const networkStatus = useWatch<EvaluationFormType, 'billingInfo.insuranceData.networkStatus'>({
    name: 'billingInfo.insuranceData.networkStatus',
  });
  const paymentType = useWatch<EvaluationFormType, 'billingInfo.type'>({ name: 'billingInfo.type' });
  const eligibilityResult = useWatch<EvaluationFormType, 'billingInfo.eligibilityCheckResult'>({
    name: 'billingInfo.eligibilityCheckResult',
  });
  const { handleSubmit, setError, register } = useFormContext<EvaluationFormType>();
  const { t } = useTranslation();

  const { filters, onContinueToScheduleSts, isResumedLead } = useSignupDirectCtx();
  if (!filters) throw 'Expected filters to be defined';

  // In this case insurance form renders the submit eligibility check button
  if (
    paymentTypeIsInsurance(paymentType) &&
    eligibilityResult !== EligibilityCheckResult.IN_NETWORK &&
    networkStatus === 'In-network'
  ) {
    return null;
  }

  const termsUrl = config.get('termsUrl') as string;
  const privacyPolicyUrl = config.get('privacyPolicyUrl') as string;
  const speechTermsUrl = config.get('speechTermsUrl') as string;
  const noticePrivacyUrl = config.get('privacyUrl') as string;

  return (
    <div className="flex flex-col gap-e32">
      {paymentType === 'insurance-commercial' && (
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.eval.form.riskFree.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            <Trans i18nKey="directSignup.eval.form.riskFree.description">
              <Link to={termsUrl} external>
                <></>
              </Link>
            </Trans>
          </Paragraph>
        </HeadingCombo>
      )}

      <Checkbox variant="plain" id="risk-free-checkbox" {...register('consentToTerms')}>
        <Paragraph variant="secondary" size="sm" data-testid="terms-paragraph">
          {t('directSignup.eval.form.terms.1')}

          <Link to={termsUrl} external>
            {t('directSignup.eval.form.terms.2')}
          </Link>
          {t('directSignup.eval.form.terms.3')}
          <Link to={noticePrivacyUrl} external>
            {t('directSignup.eval.form.terms.4')}
          </Link>
          {t('directSignup.eval.form.terms.5')}
          <Link to={speechTermsUrl} external>
            {t('directSignup.eval.form.terms.6')}
          </Link>
          {t('directSignup.eval.form.terms.7')}
          <Link to={privacyPolicyUrl} external>
            {t('directSignup.eval.form.terms.8')}
          </Link>
          {t('directSignup.eval.form.terms.9')}
          {t('directSignup.eval.form.terms.10')}
        </Paragraph>
      </Checkbox>

      <FormLayout.GlobalErrors />
      <FormLayout.CTA>
        <SubmitButton
          data-testid="confirm-reservation-submit-button"
          onClick={handleSubmit(async data => {
            if (!data.consentToTerms) {
              setError('consentToTerms', { message: t('errors.acceptTerms') });
              return;
            }
            await submitEvalForm({
              data,
              filters,
              setError,
              onContinueToScheduleSts,
              isResumedLead,
            });
          })}
        >
          {t('directSignup.eval.form.submit')}
        </SubmitButton>
      </FormLayout.CTA>
    </div>
  );
};

export const DirectSignupEvaluationForm = Component;
